import React, { useState, useEffect, useMemo } from 'react';
import { Box, Drawer, List, ListItem, ListItemIcon, ListItemText, Typography, AppBar, Toolbar, IconButton, Avatar } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import StorageIcon from '@mui/icons-material/Storage';
import PeopleIcon from '@mui/icons-material/People';
import GroupIcon from '@mui/icons-material/Group';
import SettingsIcon from '@mui/icons-material/Settings';
import RuleIcon from '@mui/icons-material/Rule';
import MenuIcon from '@mui/icons-material/Menu';
import { useKBTheme } from '../../hooks/useKBTheme';

export const AnimatedAdminConsoleDemo: React.FC = () => {
  const { mode } = useKBTheme();
  const [selectedSection, setSelectedSection] = useState('dashboard');
  const [drawerOpen, setDrawerOpen] = useState(true);

  const menuItems = useMemo(() => [
    { text: 'Dashboard', icon: <DashboardIcon />, id: 'dashboard' },
    { text: 'Documents', icon: <StorageIcon />, id: 'documents' },
    { text: 'Users', icon: <PeopleIcon />, id: 'users' },
    { text: 'Teams', icon: <GroupIcon />, id: 'teams' },
    { text: 'Roles', icon: <SettingsIcon />, id: 'roles' },
    { text: 'Smart Policies', icon: <RuleIcon />, id: 'smart-policies' },
  ], []);

  useEffect(() => {
    // Auto-cycle through sections
    const interval = setInterval(() => {
      const currentIndex = menuItems.findIndex(item => item.id === selectedSection);
      const nextIndex = (currentIndex + 1) % menuItems.length;
      setSelectedSection(menuItems[nextIndex].id);
    }, 3000);

    return () => clearInterval(interval);
  }, [selectedSection, menuItems]);

  return (
    <Box sx={{ display: 'flex', height: '100%', background: mode === 'dark' ? '#0f172a' : '#f8fafc' }}>
      {/* Sidebar */}
      <Drawer
        variant="permanent"
        open={drawerOpen}
        sx={{
          width: drawerOpen ? 240 : 0,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 240,
            boxSizing: 'border-box',
            background: mode === 'dark' ? '#1a2332' : '#ffffff',
            borderRight: `1px solid ${mode === 'dark' ? 'rgba(71, 85, 105, 0.4)' : 'rgba(226, 232, 240, 0.8)'}`,
            transition: 'width 0.3s',
          },
        }}
      >
        <Box sx={{ p: 2, borderBottom: `1px solid ${mode === 'dark' ? 'rgba(71, 85, 105, 0.4)' : 'rgba(226, 232, 240, 0.8)'}` }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: mode === 'dark' ? '#fff' : '#0f172a' }}>
            Admin Console
          </Typography>
        </Box>
        <List sx={{ pt: 2 }}>
          {menuItems.map((item) => (
            <ListItem
              key={item.id}
              onClick={() => setSelectedSection(item.id)}
              sx={{
                mb: 1,
                mx: 2,
                borderRadius: 2,
                cursor: 'pointer',
                backgroundColor: selectedSection === item.id
                  ? mode === 'dark' ? 'rgba(99, 102, 241, 0.2)' : 'rgba(99, 102, 241, 0.1)'
                  : 'transparent',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: mode === 'dark' ? 'rgba(99, 102, 241, 0.15)' : 'rgba(99, 102, 241, 0.08)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: selectedSection === item.id ? '#6366f1' : mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#64748b',
                  minWidth: 40,
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  sx: {
                    fontWeight: selectedSection === item.id ? 600 : 500,
                    color: selectedSection === item.id ? '#6366f1' : mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569',
                  },
                }}
              />
            </ListItem>
          ))}
        </List>
      </Drawer>

      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <AppBar
          position="static"
          elevation={0}
          sx={{
            background: mode === 'dark' ? '#1a2332' : '#ffffff',
            borderBottom: `1px solid ${mode === 'dark' ? 'rgba(71, 85, 105, 0.4)' : 'rgba(226, 232, 240, 0.8)'}`,
          }}
        >
          <Toolbar>
            <IconButton onClick={() => setDrawerOpen(!drawerOpen)}>
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 600, color: mode === 'dark' ? '#fff' : '#0f172a' }}>
              {menuItems.find(item => item.id === selectedSection)?.text}
            </Typography>
            <Avatar sx={{ bgcolor: '#6366f1', width: 32, height: 32 }}>
              <PeopleIcon sx={{ fontSize: 18 }} />
            </Avatar>
          </Toolbar>
        </AppBar>

        <Box sx={{ flex: 1, p: 4, overflow: 'auto' }}>
          <Box
            sx={{
              animation: 'fadeIn 0.4s ease-out',
              '@keyframes fadeIn': {
                '0%': { opacity: 0, transform: 'translateY(10px)' },
                '100%': { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: mode === 'dark' ? '#fff' : '#0f172a' }}>
              {menuItems.find(item => item.id === selectedSection)?.text}
            </Typography>
            <Typography variant="body1" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#64748b', mb: 4 }}>
              Manage your {menuItems.find(item => item.id === selectedSection)?.text.toLowerCase()} settings and configurations from this page.
            </Typography>

            {/* Content Cards */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 3 }}>
              {[1, 2, 3].map((i) => (
                <Box
                  key={i}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    background: mode === 'dark'
                      ? 'linear-gradient(135deg, rgba(15, 15, 15, 0.95) 0%, rgba(25, 25, 25, 0.95) 100%)'
                      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%)',
                    border: `1px solid ${mode === 'dark' ? 'rgba(71, 85, 105, 0.3)' : 'rgba(226, 232, 240, 0.8)'}`,
                    animation: `slideIn 0.4s ease-out ${i * 0.1}s both`,
                    '@keyframes slideIn': {
                      '0%': { opacity: 0, transform: 'translateX(-20px)' },
                      '100%': { opacity: 1, transform: 'translateX(0)' },
                    },
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: mode === 'dark' ? '#fff' : '#0f172a' }}>
                    Section {i}
                  </Typography>
                  <Typography variant="body2" sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#64748b' }}>
                    Configure and manage your {menuItems.find(item => item.id === selectedSection)?.text.toLowerCase()} options here.
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};








